import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './FlowsList.css';

const FlowsList = () => {
  usePageTitle('Flows');
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [datasetFilter, setDatasetFilter] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteModal, setDeleteModal] = useState({ show: false, flow: null });
  const [deleting, setDeleting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [sortColumn, setSortColumn] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const navigate = useNavigate();

  useEffect(() => {
    fetchFlows();
  }, [filter, typeFilter, datasetFilter, searchQuery]);

  // Debounced search - triggers 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
  };

  const fetchFlows = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/etl/flows/');
      let filteredFlows = response.data;

      // Status filter
      if (filter !== 'all') {
        filteredFlows = filteredFlows.filter(flow => flow.status === filter);
      }

      // Flow type filter (odoo/sql)
      if (typeFilter !== 'all') {
        filteredFlows = filteredFlows.filter(flow => flow.flow_type === typeFilter);
      }

      // Dataset filter (bronze=stg, silver=int, gold=analytics)
      if (datasetFilter !== 'all') {
        const datasetMap = { bronze: 'stg', silver: 'int', gold: 'analytics' };
        filteredFlows = filteredFlows.filter(flow => flow.bq_dataset === datasetMap[datasetFilter]);
      }

      // Search filter on name
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filteredFlows = filteredFlows.filter(flow =>
          flow.name.toLowerCase().includes(query) ||
          (flow.bq_table && flow.bq_table.toLowerCase().includes(query))
        );
      }

      setFlows(filteredFlows);
      setLoading(false);
    } catch (err) {
      setError('Failed to load flows');
      setLoading(false);
    }
  };

  const triggerFlow = async (flowId, e) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to trigger this flow?')) return;

    try {
      await axiosInstance.post(`/etl/flows/${flowId}/trigger/`);
      alert('Flow triggered successfully!');
      fetchFlows();
    } catch (err) {
      alert('Failed to trigger flow: ' + (err.response?.data?.error || err.message));
    }
  };

  const deleteFlow = async () => {
    if (!deleteModal.flow) return;

    setDeleting(true);
    try {
      await axiosInstance.delete(`/etl/flows/${deleteModal.flow.id}/`);
      setDeleteModal({ show: false, flow: null });
      fetchFlows();
    } catch (err) {
      alert('Failed to delete flow: ' + (err.response?.data?.error || err.message));
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  const getSuccessRate = (flow) => {
    if (flow.total_runs === 0) return null;
    return Math.round((flow.successful_runs / flow.total_runs) * 100);
  };

  const getLayerFromDataset = (dataset) => {
    if (dataset === 'stg') return 'bronze';
    if (dataset === 'int') return 'silver';
    if (dataset === 'analytics') return 'gold';
    return null;
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortValue = (flow, column) => {
    switch (column) {
      case 'name':
        return flow.name?.toLowerCase() || '';
      case 'status':
        return flow.status || '';
      case 'type':
        return flow.flow_type || '';
      case 'layer':
        return flow.bq_dataset || '';
      case 'destination':
        return flow.bq_table?.toLowerCase() || '';
      case 'success':
        return flow.total_runs > 0 ? (flow.successful_runs / flow.total_runs) : -1;
      case 'lastrun':
        return flow.last_run_at ? new Date(flow.last_run_at).getTime() : 0;
      default:
        return '';
    }
  };

  const sortedFlows = [...flows].sort((a, b) => {
    const aVal = getSortValue(a, sortColumn);
    const bVal = getSortValue(b, sortColumn);

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate summary stats from all data (not filtered)
  const stats = {
    total: flows.length,
    active: flows.filter(f => f.status === 'active').length,
    successful: flows.reduce((sum, f) => sum + f.successful_runs, 0),
    failed: flows.reduce((sum, f) => sum + f.failed_runs, 0),
    totalRuns: flows.reduce((sum, f) => sum + f.total_runs, 0),
  };

  // Pagination (use sortedFlows)
  const totalPages = Math.ceil(sortedFlows.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedFlows = sortedFlows.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, typeFilter, datasetFilter, searchQuery]);

  // Check if any filters are active
  const hasActiveFilters = filter !== 'all' || typeFilter !== 'all' || datasetFilter !== 'all' || searchQuery;

  const clearAllFilters = () => {
    setFilter('all');
    setTypeFilter('all');
    setDatasetFilter('all');
    clearSearch();
  };

  if (loading) {
    return (
      <div className="flows-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <span>Loading flows...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flows-container">
        <div className="error-state">{error}</div>
      </div>
    );
  }

  return (
    <div className="flows-container">
      {/* Delete Confirmation Modal */}
      {deleteModal.show && (
        <div className="modal-overlay" onClick={() => setDeleteModal({ show: false, flow: null })}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Flow</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteModal.flow?.name}</strong>?</p>
              <p className="modal-warning">This action cannot be undone. All run history will be deleted.</p>
            </div>
            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setDeleteModal({ show: false, flow: null })}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="btn-delete"
                onClick={deleteFlow}
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete Flow'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="page-header">
        <div className="header-content">
          <div className="header-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <div>
            <h1>Flows</h1>
            <p className="page-description">Manage and monitor your ETL data pipelines</p>
          </div>
        </div>
        <button onClick={() => navigate('/flows/new')} className="btn-primary">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 2v12M2 8h12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          New Flow
        </button>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-info">
              <div className="stat-label">Total Flows</div>
              <div className="stat-value">{stats.total}</div>
            </div>
            <div className="stat-icon stat-icon-default">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-bar stat-bar-default"></div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-info">
              <div className="stat-label">Active</div>
              <div className="stat-value">{stats.active}</div>
            </div>
            <div className="stat-icon stat-icon-primary">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-bar stat-bar-primary"></div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-info">
              <div className="stat-label">Successful Runs</div>
              <div className="stat-value">{stats.successful}/{stats.totalRuns}</div>
            </div>
            <div className="stat-icon stat-icon-success">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M22 4L12 14.01l-3-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-bar stat-bar-success"></div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-info">
              <div className="stat-label">Failed Runs</div>
              <div className="stat-value">{stats.failed}</div>
            </div>
            <div className="stat-icon stat-icon-error">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 9v4M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
          <div className="stat-bar stat-bar-error"></div>
        </div>
      </div>

      {/* Search and Filters Row */}
      <div className="search-filters-row">
        <div className="search-wrapper">
          <svg className="search-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M7.333 12.667A5.333 5.333 0 107.333 2a5.333 5.333 0 000 10.667zM14 14l-2.9-2.9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <input
            type="text"
            placeholder="Search flows by name or destination..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className="search-clear" onClick={clearSearch} type="button">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>

        <div className="filter-buttons">
          <div className="filter-dropdown">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className={`filter-btn ${filter !== 'all' ? 'active' : ''}`}
            >
              <option value="all">Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <div className="filter-dropdown">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className={`filter-btn ${typeFilter !== 'all' ? 'active' : ''}`}
            >
              <option value="all">Type</option>
              <option value="odoo">Odoo</option>
              <option value="sql">SQL</option>
              <option value="api">API</option>
            </select>
          </div>
          <div className="filter-dropdown">
            <select
              value={datasetFilter}
              onChange={(e) => setDatasetFilter(e.target.value)}
              className={`filter-btn ${datasetFilter !== 'all' ? 'active' : ''}`}
            >
              <option value="all">Layer</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
            </select>
          </div>
        </div>
      </div>

      {/* Results Info with Active Filters */}
      <div className="results-row">
        <span className="results-count">{flows.length} flow{flows.length !== 1 ? 's' : ''} found</span>
        {hasActiveFilters && (
          <div className="active-filters">
            {filter !== 'all' && (
              <span className="filter-chip">
                Status: {filter}
                <button onClick={() => setFilter('all')}>
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </span>
            )}
            {typeFilter !== 'all' && (
              <span className="filter-chip">
                Type: {typeFilter}
                <button onClick={() => setTypeFilter('all')}>
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </span>
            )}
            {datasetFilter !== 'all' && (
              <span className="filter-chip">
                Layer: {datasetFilter}
                <button onClick={() => setDatasetFilter('all')}>
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </span>
            )}
            {searchQuery && (
              <span className="filter-chip">
                Search: "{searchQuery}"
                <button onClick={clearSearch}>
                  <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
                    <path d="M10.5 3.5L3.5 10.5M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Flows Table */}
      {flows.length === 0 ? (
        <div className="empty-state">
          <h3>No flows found</h3>
          <p>Create your first ETL flow to get started</p>
          <button onClick={() => navigate('/flows/new')} className="btn-primary">
            Create Flow
          </button>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="flows-table">
              <thead>
                <tr>
                  <th className="col-status sortable" onClick={() => handleSort('status')}>
                    Status
                    {sortColumn === 'status' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-flow sortable" onClick={() => handleSort('name')}>
                    Flow
                    {sortColumn === 'name' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-type sortable" onClick={() => handleSort('type')}>
                    Type
                    {sortColumn === 'type' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-layer sortable" onClick={() => handleSort('layer')}>
                    Layer
                    {sortColumn === 'layer' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-destination sortable" onClick={() => handleSort('destination')}>
                    Destination
                    {sortColumn === 'destination' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-success sortable" onClick={() => handleSort('success')}>
                    Success
                    {sortColumn === 'success' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-lastrun sortable" onClick={() => handleSort('lastrun')}>
                    Last Run
                    {sortColumn === 'lastrun' && (
                      <span className={`sort-icon ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </th>
                  <th className="col-actions">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedFlows.map((flow) => {
                  const successRate = getSuccessRate(flow);
                  const layer = getLayerFromDataset(flow.bq_dataset);
                  return (
                    <tr
                      key={flow.id}
                      className="table-row"
                      onClick={() => navigate(`/flows/${flow.id}`)}
                    >
                      <td className="col-status">
                        <span className={`status-dot status-${flow.status}`}></span>
                      </td>
                      <td className="col-flow">
                        <div className="flow-info">
                          <span className="flow-name">{flow.name}</span>
                          {flow.description && (
                            <span className="flow-desc">{flow.description}</span>
                          )}
                        </div>
                      </td>
                      <td className="col-type">
                        <span className={`type-badge type-${flow.flow_type || 'odoo'}`}>
                          {flow.flow_type === 'sql' ? 'SQL' : flow.flow_type === 'api' ? 'API' : 'Odoo'}
                        </span>
                      </td>
                      <td className="col-layer">
                        {layer && (
                          <span className={`layer-badge layer-${layer}`}>
                            {layer.charAt(0).toUpperCase() + layer.slice(1)}
                          </span>
                        )}
                      </td>
                      <td className="col-destination">
                        {flow.bq_table ? (
                          <code className="destination-code">{flow.bq_table}</code>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="col-success">
                        {successRate !== null ? (
                          <div className="success-cell">
                            <div className="success-bar">
                              <div
                                className={`success-fill ${successRate >= 90 ? 'high' : successRate >= 70 ? 'medium' : 'low'}`}
                                style={{ width: `${successRate}%` }}
                              ></div>
                            </div>
                            <span className={`success-text ${successRate >= 90 ? 'high' : successRate >= 70 ? 'medium' : 'low'}`}>
                              {successRate}%
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="col-lastrun">
                        <span className="lastrun-text">{formatDate(flow.last_run_at)}</span>
                      </td>
                      <td className="col-actions">
                        <div className="action-buttons" onClick={e => e.stopPropagation()}>
                          <button
                            className="action-btn run"
                            onClick={(e) => triggerFlow(flow.id, e)}
                            disabled={flow.status !== 'active'}
                            title="Run flow"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M5 3l14 9-14 9V3z" fill="currentColor"/>
                            </svg>
                          </button>
                          <button
                            className="action-btn view"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/flows/${flow.id}`);
                            }}
                            title="View details"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
                            </svg>
                          </button>
                          <button
                            className="action-btn delete"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteModal({ show: true, flow });
                            }}
                            title="Delete flow"
                          >
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                              <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <span className="pagination-info">
                Page {currentPage} of {totalPages}
              </span>
              <button
                className="pagination-btn"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default FlowsList;
